import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { GraduationCap, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import TutorialCard, { TutorialEmbed } from "@/components/academy/TutorialCard";

/**
 * BusinessAcademyPage — the always-on education destination for TBC
 * business owners. Organised into 5 categories with tutorial cards.
 *
 * Cards without a video_url render a "Coming Soon" placeholder so the
 * layout stays polished before we've recorded content. Clicking a card
 * opens a modal player that supports YouTube + Vimeo.
 */
export default function BusinessAcademyPage() {
  const [tutorials, setTutorials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [params] = useSearchParams();
  const focusSlug = params.get("tutorial");

  useEffect(() => {
    axios
      .get(`${API}/academy/tutorials?active_only=true`)
      .then((r) => {
        setTutorials(r.data.tutorials || []);
        setCategories(r.data.categories || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Auto-open a tutorial if ?tutorial=slug in the URL (deep-linked from
  // Contextual Help "Learn more" chips).
  useEffect(() => {
    if (!focusSlug || tutorials.length === 0) return;
    const t = tutorials.find((x) => x.slug === focusSlug);
    if (t) setSelected(t);
  }, [focusSlug, tutorials]);

  const byCategory = (slug) => tutorials.filter((t) => t.category === slug);
  const featured = tutorials.find((t) => t.featured) || tutorials[0];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="business-academy-page">
      <Navbar />

      {/* Hero */}
      <section className="bg-tbc-navy text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
          <Link
            to="/dashboard"
            className="inline-flex items-center text-white/70 hover:text-white text-sm mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Dashboard
          </Link>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-tbc-gold/20 text-tbc-gold flex items-center justify-center flex-none">
              <GraduationCap className="w-6 h-6" />
            </div>
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-tbc-gold">
                TBC Business Academy
              </h1>
              <p className="text-white/80 text-sm sm:text-base font-body mt-2 max-w-2xl">
                Everything you need to set up your storefront, list products,
                get discovered on Blinterest and grow your business. Bite-sized
                tutorials + step-by-step guides.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured tutorial */}
      {featured && !loading && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 lg:-mt-10 relative z-10">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 sm:p-6">
            <p className="text-tbc-gold text-[10px] uppercase tracking-widest font-semibold mb-2">
              Featured Tutorial
            </p>
            <div className="grid lg:grid-cols-5 gap-4 lg:gap-6">
              <div className="lg:col-span-3">
                <TutorialEmbed
                  videoUrl={featured.video_url}
                  thumbnailUrl={featured.thumbnail_url}
                  title={featured.title}
                />
              </div>
              <div className="lg:col-span-2 flex flex-col justify-center">
                <h2 className="font-heading text-2xl text-tbc-navy">
                  {featured.title}
                </h2>
                <p className="text-gray-600 text-sm mt-2 leading-relaxed">
                  {featured.description}
                </p>
                <Button
                  onClick={() => setSelected(featured)}
                  className="mt-4 self-start bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy rounded-full"
                  data-testid="academy-featured-open"
                >
                  Watch now
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Sections */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 animate-pulse">
            {[1,2,3,4,5,6,7,8].map((i) => (
              <div key={i} className="aspect-video bg-gray-200 rounded-2xl" />
            ))}
          </div>
        ) : (
          <div className="space-y-10">
            {categories.map((cat) => {
              const list = byCategory(cat.slug);
              if (list.length === 0) return null;
              return (
                <div key={cat.slug} data-testid={`academy-cat-${cat.slug}`}>
                  <h2 className="font-heading text-xl sm:text-2xl text-tbc-navy mb-3">
                    {cat.label}
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {list.map((t) => (
                      <TutorialCard
                        key={t.tutorial_id}
                        tutorial={t}
                        onOpen={setSelected}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <Footer />

      {/* Tutorial modal */}
      <Dialog open={Boolean(selected)} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent
          className="sm:max-w-3xl"
          data-testid="academy-tutorial-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-tbc-navy text-xl">
              {selected?.title}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3">
              <TutorialEmbed
                videoUrl={selected.video_url}
                thumbnailUrl={selected.thumbnail_url}
                title={selected.title}
              />
              {selected.description && (
                <p className="text-gray-700 text-sm leading-relaxed">
                  {selected.description}
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
